import React, { FC, useState, useEffect } from "react"
import { Button, Form, Table } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { FieldArray, Formik } from "formik"
import _ from "lodash"

import { formatIn } from "@/helpers/units"

import FormikBootstrap from "@/components/formik-bootstrap"
import {
  getStops,
  getDefaultRateCategories,
  getField,
  isOperatorTourIdea,
  getFeatures,
  getLinkableComponentOptions,
} from "@/onboarding/features/product/selectors"
import {
  appendFieldListItem,
  removeFieldListItem,
  updateFieldListItem,
} from "@/onboarding/features/product/Slice"
import { newComponent, ComponentType } from "@/onboarding/features/product/models/Component"
import {
  ReservedFields,
  ReferencedValueTypes,
  CRUISE_LINE_FIELD_PLACEHOLDER,
} from "@/onboarding/features/product/models/Product"
import ReferencedValueSelect from "@/onboarding/features/product/components/shared/ReferencedValueSelect"
import RateInputs from "@/onboarding/features/product/components/shared/RateInputs"
import TextDisplay from "@/onboarding/features/product/components/shared/TextDisplay"
import ReferencedValueDisplay from "@/onboarding/features/product/components/shared/ReferencedValueDisplay"
import { BlueLock, CloseIcon, EditIcon } from "@/components/Icons"
import OperatorOnly from "@/auth_descriptor/OperatorOnly"
import LoadingSpinner from "@/components/LoadingSpinner"
import { useProductSubmitting } from "@/onboarding/features/product/helpers"

const ComponentEdit = ({ index }): JSX.Element => (
  <div className="d-flex flex-column gap-3">
    <FormikBootstrap.TextInput name={`components.${index}.name`} label="Name" />
    <FormikBootstrap.TextInput name={`components.${index}.description`} label="Description" />
    <RateInputs name={`components.${index}.ratesheet`} />
  </div>
)

const LinkedComponents = ({ linkableComponents }): JSX.Element => {
  if (_.isEmpty(linkableComponents)) {
    return (
      <>
        <div className="fw-bold">Linked Components</div>
        <p className="text-danger">
          There are no linkable components for this tour. Please add or import components on the
          tour page before linking.
        </p>
      </>
    )
  }

  return (
    <FormikBootstrap.MultiSelect
      name="linkedComponentIds"
      label="Linked Components"
      options={linkableComponents}
    />
  )
}

const ComponentList = ({ values, arrayHelpers }): JSX.Element => {
  const [editingComponent, setEditingComponent] = useState(null)
  const defaultRateCategories = useSelector(getDefaultRateCategories)
  const features = useSelector(getFeatures)

  return (
    <div>
      <div className="d-flex justify-content-between align-content-center w-100">
        <h2 className="h5 mb-0">Components and Rates</h2>
        <Button
          className="hover-underline cursor-pointer"
          disabled={!features.general.product.componentsEditable}
          onClick={() => {
            const newIndex = values.components.length
            arrayHelpers.push(newComponent({ defaultRateCategories, kind: ComponentType.SIMPLE }))
            setEditingComponent(newIndex)
          }}
        >
          Add Component
        </Button>
      </div>
      <Table>
        <thead>
          <tr>
            <th>Component Name</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {_.isEmpty(values.components) && (
            <tr>
              <td colSpan={2}>No components loaded</td>
            </tr>
          )}
          {values.components.map((component, index) => (
            <React.Fragment key={index}>
              <tr>
                <td>
                  <TextDisplay value={component.name} />
                </td>
                <td>
                  {features.general.product.componentsEditable && (
                    <div className="d-flex justify-content-end gap-3">
                      <span
                        className="cursor-pointer"
                        onClick={() => {
                          console.log("editingComponent === index:", editingComponent === index)
                          setEditingComponent(index === editingComponent ? null : index)
                        }}
                      >
                        {editingComponent !== index ? <EditIcon /> : <CloseIcon />}
                      </span>
                      <span
                        className="cursor-pointer text-danger"
                        onClick={() => arrayHelpers.remove(index)}
                      >
                        <i className="fas fa-trash" />
                      </span>
                    </div>
                  )}
                </td>
              </tr>
              {index === editingComponent && (
                <tr>
                  <td colSpan={2}>
                    <ComponentEdit index={index} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const StopEditForm = ({ stop, index, onCancel }): JSX.Element => {
  const dispatch = useDispatch()
  const [isSubmitting, setSubmitting] = useProductSubmitting()
  const isTourOperatorIdea = useSelector(isOperatorTourIdea)
  const linkableComponents = useSelector(getLinkableComponentOptions)
  const features = useSelector(getFeatures)
  const cruiseLineFieldMessage = isTourOperatorIdea ? CRUISE_LINE_FIELD_PLACEHOLDER : ""

  return (
    <>
      <h5>Edit Segment</h5>
      <Formik
        enableReinitialize
        initialValues={stop}
        onSubmit={async (values) => {
          setSubmitting(true)
          await dispatch(updateFieldListItem({ fieldName: stop.fieldName, index, value: values }))
          onCancel()
        }}
      >
        {({ values, handleSubmit, dirty, isSubmitting }) => (
          <Form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
            <FormikBootstrap.TextInput name="name" label="Name (optional)" />
            <FormikBootstrap.TextInput name="description" label="Description (optional)" />
            <div className="row">
              <div className="col-md-6">
                <FormikBootstrap.TextInput name="time" label="Time" />
              </div>
              <div className="col-md-6">
                <FormikBootstrap.NumberInput name="duration" label="Duration (Minutes)" />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <ReferencedValueSelect
                  name="stopType"
                  type={ReferencedValueTypes.STOP_TYPE}
                  label="Type"
                  emptyMessage={cruiseLineFieldMessage}
                />
              </div>
              <div className="col-md-6">
                <ReferencedValueSelect
                  name="transferType"
                  type={ReferencedValueTypes.VEHICLE_TYPE}
                  label="Transfer Type"
                  emptyMessage={cruiseLineFieldMessage}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6">
                <FormikBootstrap.NumberInput name="steps" label="Steps" />
              </div>
              <div className="col-md-6">
                <FormikBootstrap.TextInput name="terrain" label="Terrain" />
              </div>
            </div>
            <LinkedComponents linkableComponents={linkableComponents} />
            <FormikBootstrap.Switch name="bathroomsAvailable" label="Bathrooms Available?" />
            <FieldArray
              name="components"
              render={(arrayHelpers) => (
                <ComponentList values={values} arrayHelpers={arrayHelpers} />
              )}
            />
            <div className="d-flex gap-3">
              <Button type="submit" disabled={isSubmitting || !dirty}>
                Save
              </Button>
              <Button variant="danger" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              {isSubmitting && <LoadingSpinner />}
            </div>
          </Form>
        )}
      </Formik>
    </>
  )
}

const Edit: FC = () => {
  const [editingStop, setEditingStop] = useState(null)
  const [indexForNewStop, setIndexForNewStop] = useState(null)
  const dispatch = useDispatch()
  const stops = useSelector(getStops)
  const defaultRateCategories = useSelector(getDefaultRateCategories)
  const field = useSelector(getField(ReservedFields.TOUR_STOPS))
  const [isSubmitting, setSubmitting] = useProductSubmitting()

  const addStop = (index) => {
    setSubmitting(true)

    dispatch(
      appendFieldListItem({
        fieldName: ReservedFields.TOUR_STOPS,
        value: newComponent({ defaultRateCategories, kind: ComponentType.STOP }),
      })
    )

    setIndexForNewStop(index)
  }

  useEffect(() => {
    if (indexForNewStop !== null && stops.length > indexForNewStop) {
      setEditingStop(indexForNewStop)
      setIndexForNewStop(null)
    }
  }, [stops, indexForNewStop])

  const cancelEdit = () => setEditingStop(null)

  if (editingStop !== null) {
    return <StopEditForm stop={stops[editingStop]} index={editingStop} onCancel={cancelEdit} />
  }

  return (
    <>
      <div className="d-flex align-items-center">
        {!field.writeable && (
          <BlueLock
            className="me-2 cursor-pointer"
            data-toggle="tooltip"
            data-placement="top"
            title="This value is fixed and cannot be edited"
          />
        )}
        <h2 className="h5">Segments</h2>
      </div>
      <Table>
        <thead>
          <tr>
            <td>Time</td>
            <td>Duration</td>
            <td>Transfer Type</td>
            <td>Name</td>
            <th />
          </tr>
        </thead>
        <tbody>
          {stops.length === 0 && (
            <tr>
              <td colSpan={2}>
                No segments loaded
                <OperatorOnly>
                  {field.required && (
                    <span
                      data-toggle="tooltip"
                      data-placement="top"
                      title="This is an incomplete required component"
                      className="ms-2"
                      style={{ color: "red" }}
                    >
                      *
                    </span>
                  )}
                </OperatorOnly>
              </td>
              <td />
              <td />
              <td />
            </tr>
          )}
          {stops.map((stop, index) => (
            <tr key={`${stop.name}-${index}`}>
              <td>
                <TextDisplay value={stop.time} />
              </td>
              <td>
                <TextDisplay
                  value={
                    Number.isFinite(Number(stop.duration))
                      ? formatIn("duration", Number(stop.duration), "hr")
                      : stop.duration
                  }
                />
              </td>
              <td>
                <ReferencedValueDisplay value={stop.transferType} />
              </td>
              <td>
                <TextDisplay value={stop.name} />
              </td>
              <td>
                {field.writeable && (
                  <div className="d-flex justify-content-end gap-3">
                    <span className="cursor-pointer" onClick={() => setEditingStop(index)}>
                      <i className="fas fa-edit" />
                    </span>

                    <span
                      className="cursor-pointer text-danger"
                      onClick={() =>
                        dispatch(
                          removeFieldListItem({
                            fieldName: ReservedFields.TOUR_STOPS,
                            index,
                          })
                        )
                      }
                    >
                      <i className="fas fa-trash" />
                    </span>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {field.writeable && (
        <div className="d-flex gap-2">
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => addStop(stops.length)}
            disabled={isSubmitting}
          >
            <i className="fas fa-plus me-2" />
            Add Segment
          </Button>
          {isSubmitting && <LoadingSpinner />}
        </div>
      )}
    </>
  )
}

export default Edit
